import React from 'react';
import { FormattedMessage } from 'react-intl';
import { BulkActions } from '../../enums/bulk-actions';
import { BulkFulfillmentPlanTypeEnum } from '../../enums/bulk-fulfillment-plan-type';
import { messages } from './messages';

export const BulkActionJobErrorType = {
  InlineError: 'inlineError',
  ModalError: 'modalError',
};

const bulkActionModalJobValidators = {
  [BulkActions.customsDeclarations.name]: ({ bulkFulfillmentPlanType }) => {
    if (bulkFulfillmentPlanType === BulkFulfillmentPlanTypeEnum.Domestic) {
      return {
        message: {
          title: <FormattedMessage {...messages.bulkEditErrorTitle} />,
          message: <FormattedMessage {...messages.customsErrorMessage} />,
        },
      };
    }
  },
  [BulkActions.otherShippingOptions.name]: ({ services }) => {
    if (!services || services.length === 0) {
      return {
        message: {
          title: <FormattedMessage {...messages.bulkEditErrorTitle} />,
          message: <FormattedMessage {...messages.otherShippingOptionsErrorMessage} />,
        },
      };
    }
  },
  [BulkActions.service.name]: ({ bulkFulfillmentPlanType }) => {
    if (bulkFulfillmentPlanType === BulkFulfillmentPlanTypeEnum.Mixed) {
      return {
        message: {
          title: <FormattedMessage {...messages.bulkEditErrorTitle} />,
          message: <FormattedMessage {...messages.servicesErrorMessage} />,
        },
      };
    }
  },
  [BulkActions.confirmation.name]: ({ fulfillmentPlanCount, bulkFulfillmentPlanShippingStrategiesCount }, activeBulkValidations) => {
    const emptyChanges = fulfillmentPlanCount === bulkFulfillmentPlanShippingStrategiesCount;
    const confirmationBulkActionEnabled = activeBulkValidations?.ignoreConfirmationBulkUpdate || false;

    return (
      emptyChanges &&
      confirmationBulkActionEnabled && {
        message: {
          title: <FormattedMessage {...messages.bulkEditErrorTitle} />,
          message: <FormattedMessage {...messages.bulkUpdateModalConfirmationShippingStrategiesError} />,
        },
      }
    );
  },
  [BulkActions.packageBulkAction.name]: ({ fulfillmentPlanCount, bulkFulfillmentPlanShippingStrategiesCount }, activeBulkValidations) => {
    const emptyChanges = fulfillmentPlanCount === bulkFulfillmentPlanShippingStrategiesCount;
    const packageBulkActionEnabled = activeBulkValidations?.ignorePackageBulkUpdate || false;
    return (
      emptyChanges &&
      packageBulkActionEnabled && {
        message: {
          title: <FormattedMessage {...messages.bulkEditErrorTitle} />,
          message: <FormattedMessage {...messages.bulkUpdateModalPackageShippingStrategiesError} />,
        },
      }
    );
  },
  [BulkActions.insurance.name]: ({ hasMultiplePackages }, _activeBulkValidations, config) => {
    if (hasMultiplePackages && config.enableMultiPackageShipments) {
      return {
        message: {
          title: <FormattedMessage {...messages.bulkEditErrorTitle} />,
          message: <FormattedMessage {...messages.insuranceMultiplePackagesErrorMessage} />,
        },
      };
    }
  },
  [BulkActions.weight.name]: ({ hasMultiplePackages }, _activeBulkValidations, config) => {
    if (hasMultiplePackages && config.enableMultiPackageShipments) {
      return {
        message: {
          title: <FormattedMessage {...messages.bulkEditErrorTitle} />,
          message: <FormattedMessage {...messages.weightMultiplePackagesErrorMessage} />,
        },
      };
    }
  },
  [BulkActions.size.name]: ({ hasMultiplePackages }, _activeBulkValidations, config) => {
    if (hasMultiplePackages && config.enableMultiPackageShipments) {
      return {
        message: {
          title: <FormattedMessage {...messages.bulkEditErrorTitle} />,
          message: <FormattedMessage {...messages.sizeMultiplePackagesErrorMessage} />,
        },
      };
    }
  },
  [BulkActions.adjustWeight.name]: ({ hasMultiplePackages }, _activeBulkValidations, config) => {
    if (hasMultiplePackages && config.enableMultiPackageShipments) {
      return {
        message: {
          title: <FormattedMessage {...messages.bulkEditErrorTitle} />,
          message: <FormattedMessage {...messages.weightMultiplePackagesErrorMessage} />,
        },
      };
    }
  },
};

const bulkActionInlineJobValidators = {
  [BulkActions.confirmation.name]: ({ bulkFulfillmentPlanShippingStrategiesCount }, activeBulkValidations) => {
    const confirmationBulkActionEnabled = activeBulkValidations?.ignoreConfirmationBulkUpdate || false;
    return (
      confirmationBulkActionEnabled &&
      bulkFulfillmentPlanShippingStrategiesCount > 0 && {
        message: {
          title: '',
          message: <FormattedMessage {...messages.bulkUpdateDrawerShippingStrategiesWarning} />,
        },
      }
    );
  },
  [BulkActions.packageBulkAction.name]: ({ bulkFulfillmentPlanShippingStrategiesCount }, activeBulkValidations) => {
    const packageBulkActionEnabled = activeBulkValidations?.ignorePackageBulkUpdate || false;
    return (
      packageBulkActionEnabled &&
      bulkFulfillmentPlanShippingStrategiesCount > 0 && {
        message: {
          title: '',
          message: <FormattedMessage {...messages.bulkUpdateDrawerShippingStrategiesWarning} />,
        },
      }
    );
  },
};

const getValidatorForBulkAction = (bulkAction, errorType) => {
  const validatorFn =
    errorType === BulkActionJobErrorType.ModalError ? bulkActionModalJobValidators[bulkAction] : bulkActionInlineJobValidators[bulkAction];

  return validatorFn || (() => undefined);
};

// TODO: This constructor and configure method will be removed in Jira SPD-20698
// this is a temporary solution to allow the enableMultiPackageShipments to be passed to the validator
class BulkActionJobValidatorClass {
  constructor() {
    this.config = {
      enableMultiPackageShipments: false,
    };
  }

  setConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    return this;
  }

  getErrorForJobData(bulkActions, jobData, activeBulkValidations, errorType) {
    for (let i = 0; i < bulkActions.length; i++) {
      const bulkAction = bulkActions[i];
      const result = getValidatorForBulkAction(bulkAction.name, errorType)(jobData, activeBulkValidations, this.config);
      if (result) {
        return result;
      }
    }
  }

  validate(bulkActions, jobData, activeBulkValidations = {}, errorType = BulkActionJobErrorType.ModalError) {
    return new Promise((resolve, reject) => {
      const validationError = this.getErrorForJobData(bulkActions, jobData, activeBulkValidations, errorType);
      if (validationError) {
        reject(validationError.message);
      } else {
        resolve();
      }
    });
  }
}

export const BulkActionJobValidator = new BulkActionJobValidatorClass();
